import React from 'react';
import { createRoot } from 'react-dom/client';
import { Order } from '@/types/index';

interface PrintReceiptProps {
  order: Order;
  restaurantName?: string;
  restaurantLogo?: string;
  branchAddress?: string;
  branchPhone?: string;
  currency?: string;
}

interface PrintKitchenTicketProps {
  order: Order;
  branchName?: string;
}

// Receipt Component
const PrintReceipt: React.FC<PrintReceiptProps> = ({ 
  order, 
  restaurantName = "Menu Maven Restaurant",
  restaurantLogo,
  branchAddress = "123 Restaurant Street, City",
  branchPhone = "+1 (555) 123-4567",
  currency = "USD"
}) => {
  const formatPrice = (price: number) => `${currency} ${price.toFixed(2)}`;
  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  return (
    <div style={{ 
      fontFamily: 'Courier New, monospace', 
      width: '100%',
      maxWidth: '100%',
      margin: '0 auto',
      padding: '0',
      fontSize: '13px',
      lineHeight: '1.3',
      backgroundColor: '#fff',
      color: '#000',
      textAlign: 'center'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '12px', borderBottom: '2px solid #000', paddingBottom: '8px' }}>
        {restaurantLogo && (
          <img 
            src={restaurantLogo} 
            alt="Restaurant Logo" 
            style={{ maxWidth: '60px', height: 'auto', marginBottom: '6px' }}
          />
        )}
        <h1 style={{ fontSize: '18px', margin: '0', fontWeight: 'bold' }}>
          {restaurantName}
        </h1>
        {branchAddress && (
          <p style={{ margin: '3px 0', fontSize: '12px' }}>{branchAddress}</p>
        )}
        {branchPhone && (
          <p style={{ margin: '3px 0', fontSize: '12px' }}>{branchPhone}</p>
        )}
      </div>

      {/* Order Info */}
      <div style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', padding: '12px 0', marginBottom: '15px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
          <span><strong>Order #:</strong></span>
          <span>{order.order_number}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
          <span><strong>Date:</strong></span>
          <span>{new Date(order.created_at || '').toLocaleDateString()}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
          <span><strong>Time:</strong></span>
          <span>{new Date(order.created_at || '').toLocaleTimeString()}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
          <span><strong>Status:</strong></span>
          <span style={{ textTransform: 'capitalize' }}>{order.status}</span>
        </div>
        {order.customer_name && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
            <span><strong>Customer:</strong></span>
            <span>{order.customer_name}</span>
          </div>
        )}
        {order.customer_phone && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
            <span><strong>Phone:</strong></span>
            <span>{order.customer_phone}</span>
          </div>
        )}
        {order.order_type === 'delivery' && order.customer_address && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px' }}>
            <span><strong>Address:</strong></span>
            <span style={{ maxWidth: '180px', textAlign: 'right' }}>{order.customer_address}</span>
          </div>
        )}
        {order.order_type === 'dine_in' && order.table_number && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
            <span><strong>Table:</strong></span>
            <span>{order.table_number}</span>
          </div>
        )}
        {order.order_type && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
            <span><strong>Type:</strong></span>
            <span style={{ textTransform: 'capitalize' }}>{order.order_type}</span>
          </div>
        )}
      </div>

      {/* Items */}
      <div style={{ marginBottom: '15px' }}>
        <h3 style={{ fontSize: '16px', margin: '0 0 12px 0', borderBottom: '2px solid #000', paddingBottom: '6px', textAlign: 'center' }}>
          ORDER ITEMS
        </h3>
        {(order.order_items || []).filter(i => !i.is_removed).map((item, index, arr) => (
          <div key={item.id} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: index < arr.length - 1 ? '1px dotted #ccc' : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontWeight: 'bold', fontSize: '14px', flexGrow: 1, paddingRight: '10px' }}>{item.menu_items.name}</span>
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{formatPrice(item.total_price)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginLeft: '10px' }}>
              <span>{item.quantity} x {formatPrice(item.unit_price)}</span>
            </div>
            {item.notes && (
              <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic', marginTop: '4px', marginLeft: '10px' }}>
                Note: {item.notes}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Total */}
      <div style={{ borderTop: '1px solid #000', paddingTop: '5px', marginBottom: '8px' }}>
        {(() => {
          // Use order's calculated amounts if available, otherwise calculate from non-removed items
          const visibleItems = (order.order_items || []).filter(i => !i.is_removed)
          const subtotal = order.subtotal_amount || visibleItems.reduce((sum, item) => sum + item.total_price, 0)
          const serviceAmount = order.service_amount || 0
          const taxAmount = order.tax_amount || 0
          const deliveryFee = order.delivery_fee || 0
          // Always compute the final total from current visible items and surcharges
          const finalTotal = subtotal + serviceAmount + taxAmount + deliveryFee
          
          return (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                <span>Subtotal:</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              
              {serviceAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                  <span>Service:</span>
                  <span>{formatPrice(serviceAmount)}</span>
                </div>
              )}
              
              {taxAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                  <span>Tax:</span>
                  <span>{formatPrice(taxAmount)}</span>
                </div>
              )}
              
              {deliveryFee > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                  <span>Delivery:</span>
                  <span>{formatPrice(deliveryFee)}</span>
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 'bold', borderTop: '2px solid #000', paddingTop: '6px', marginTop: '6px' }}>
                <span>TOTAL:</span>
                <span>{formatPrice(finalTotal)}</span>
              </div>
            </>
          );
        })()}
      </div>

      {/* Special Instructions */}
      {order.notes && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '13px', margin: '0 0 4px 0', fontWeight: 'bold' }}>Notes:</div>
          <p style={{ fontSize: '12px', margin: '0', fontStyle: 'italic' }}>{order.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: '12px', borderTop: '2px solid #000', paddingTop: '8px' }}>
        <p style={{ margin: '0', fontSize: '14px', fontWeight: 'bold' }}>Thank you!</p>
        <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#666' }}>
          {new Date().toLocaleString()}
        </p>
      </div>
    </div>
  );
};

// Kitchen Ticket Component
const PrintKitchenTicket: React.FC<PrintKitchenTicketProps> = ({ 
  order, 
  branchName = "Menu Maven Restaurant"
}) => {
  const formatTime = (dateString: string) => new Date(dateString).toLocaleTimeString();

  return (
    <div style={{ 
      fontFamily: 'Courier New, monospace', 
      width: '100%',
      maxWidth: '100%',
      margin: '0 auto',
      padding: '0',
      fontSize: '14px',
      lineHeight: '1.3',
      backgroundColor: '#fff',
      border: '2px solid #000',
      textAlign: 'center'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '12px', borderBottom: '3px solid #000', paddingBottom: '8px' }}>
        <h1 style={{ fontSize: '20px', margin: '0', fontWeight: 'bold' }}>
          🍽️ KITCHEN TICKET
        </h1>
        <p style={{ margin: '5px 0', fontSize: '14px' }}>{branchName}</p>
      </div>

      {/* Order Info */}
      <div style={{ marginBottom: '12px', backgroundColor: '#f0f0f0', padding: '8px', border: '2px solid #000' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '16px', fontWeight: 'bold' }}>
          <div>ORDER: #{order.order_number}</div>
          <div>TIME: {formatTime(order.created_at || '')}</div>
        </div>
        {order.customer_name && (
          <div style={{ marginTop: '6px', fontSize: '14px' }}>
            <strong>CUSTOMER:</strong> {order.customer_name}
          </div>
        )}
      </div>

      {/* Items for Kitchen */}
      <div style={{ marginBottom: '12px' }}>
        <h3 style={{ 
          fontSize: '18px', 
          margin: '0 0 10px 0', 
          textAlign: 'center', 
          backgroundColor: '#000', 
          color: '#fff', 
          padding: '8px',
          fontWeight: 'bold'
        }}>
          🔥 ITEMS TO PREPARE
        </h3>
        {order.order_items.map((item, index) => (
          <div 
            key={item.id} 
            style={{ 
              marginBottom: '12px', 
              padding: '10px', 
              border: '2px solid #000',
              backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff'
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '6px'
            }}>
              <span style={{ 
                fontSize: '16px', 
                fontWeight: 'bold',
                flexGrow: 1
              }}>
                {item.menu_items.name}
              </span>
              <span style={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                backgroundColor: '#000',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: '4px'
              }}>
                QTY: {item.quantity}
              </span>
            </div>
            {item.notes && (
              <div style={{ 
                fontSize: '13px', 
                fontWeight: 'bold',
                color: '#d00',
                backgroundColor: '#ffe6e6',
                padding: '6px',
                border: '2px solid #d00',
                marginTop: '6px'
              }}>
                🚨 NOTE: {item.notes}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Special Order Notes */}
      {order.notes && (
        <div style={{ 
          marginBottom: '12px', 
          padding: '8px', 
          backgroundColor: '#ffeb3b', 
          border: '3px solid #f57f17'
        }}>
          <h4 style={{ 
            fontSize: '14px', 
            margin: '0 0 6px 0', 
            color: '#e65100', 
            fontWeight: 'bold' 
          }}>
            🚨 ORDER NOTES:
          </h4>
          <p style={{ fontSize: '13px', margin: '0', fontWeight: 'bold' }}>{order.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: '12px', borderTop: '3px solid #000', paddingTop: '8px' }}>
        <div style={{ fontSize: '14px', marginBottom: '4px' }}>
          STATUS: <strong style={{ textTransform: 'uppercase' }}>{order.status}</strong>
        </div>
        <div style={{ fontSize: '12px', color: '#666' }}>
          {new Date().toLocaleString()}
        </div>
      </div>
    </div>
  );
};

// Enhanced Print Functions with Printer Selection
const executePrint = async (printWindow: Window, content: string, title: string, printerName?: string, silent: boolean = false) => {
  return new Promise<void>((resolve) => {
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <meta charset="UTF-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              margin: 0;
              padding: 0;
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.2;
              background: white;
              color: black;
            }
            
            @media print {
              html, body {
                width: 100%;
                height: auto !important;
                margin: 0 !important;
                padding: 0 !important;
                overflow: visible !important;
                display: flex;
                justify-content: center;
                align-items: flex-start;
              }
              
              @page {
                size: 80mm auto; /* Thermal printer width, auto height */
                margin: 2mm; /* Small margins for better paper usage */
                padding: 0;
              }
              
              body {
                width: 76mm; /* Use more of the available width */
                max-width: 76mm;
                min-height: auto;
                margin: 0;
                padding: 2mm;
                font-size: 13px; /* Larger base font */
                box-sizing: border-box;
                display: flex;
                justify-content: center;
                align-items: flex-start;
              }
              
              .receipt-container {
                width: 100%;
                max-width: 100%;
                margin: 0 auto;
                text-align: center;
              }
              
              /* Prevent page breaks inside important elements */
              .no-break {
                page-break-inside: avoid;
                break-inside: avoid;
                width: 100%;
              }
              
              /* Force page break after receipt */
              .page-break {
                page-break-after: always;
              }
            }
            
            @media screen {
              html, body {
                margin: 0;
                padding: 0;
                display: flex;
                justify-content: center;
                align-items: flex-start;
                min-height: 100vh;
                background: #f0f0f0;
              }
              
              body {
                width: 400px; /* Wider preview */
                margin: 20px auto;
                padding: 20px;
                border: 1px solid #ccc;
                background: white;
                box-shadow: 0 0 10px rgba(0,0,0,0.1);
                border-radius: 5px;
                font-size: 14px; /* Larger preview font */
              }
              
              .receipt-container {
                width: 100%;
                text-align: center;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <div class="no-break">
              ${content}
            </div>
          </div>
          <div class="page-break"></div>
        </body>
      </html>
    `);
    printWindow.document.close();

    // Handle printer selection if specified
    if (printerName && printerName !== 'default') {
      try {
        // Store printer preference in localStorage for future use
        localStorage.setItem(`preferred_printer_${printerName}`, printerName);
        console.log(`🖨️ Attempting to print to: ${printerName}`);
      } catch (e) {
        console.warn('Could not store printer preference:', e);
      }
    }

    // Use a timeout to ensure content is loaded before printing
    setTimeout(() => {
      // Set focus to print window
      printWindow.focus();
      
      if (silent) {
        // For silent printing, try multiple approaches
        try {
          // Approach 1: Try to use a hidden iframe for silent printing
          const iframe = document.createElement('iframe');
          iframe.style.position = 'absolute';
          iframe.style.left = '-9999px';
          iframe.style.top = '-9999px';
          iframe.style.width = '1px';
          iframe.style.height = '1px';
          iframe.style.visibility = 'hidden';
          document.body.appendChild(iframe);
          
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc) {
            iframeDoc.open();
            iframeDoc.write(printWindow.document.documentElement.outerHTML);
            iframeDoc.close();
            
            // Try to print silently
            setTimeout(() => {
              try {
                // Try to print without showing dialog
                iframe.contentWindow?.print();
                console.log('🖨️ Silent printing attempted via iframe');
                
                // Clean up iframe after printing
                setTimeout(() => {
                  try {
                    document.body.removeChild(iframe);
                  } catch (e) {
                    console.warn('Could not remove iframe:', e);
                  }
                  printWindow.close();
                  resolve();
                }, 2000); // Longer timeout for silent printing
              } catch (e) {
                // Fallback to regular printing if silent fails
                console.warn('Silent printing failed, falling back to regular print:', e);
                try {
                  document.body.removeChild(iframe);
                } catch (cleanupError) {
                  console.warn('Could not remove iframe during cleanup:', cleanupError);
                }
                printWindow.print();
                setTimeout(() => {
                  printWindow.close();
                  resolve();
                }, 100);
              }
            }, 200);
          } else {
            // Fallback to regular printing
            try {
              document.body.removeChild(iframe);
            } catch (e) {
              console.warn('Could not remove iframe:', e);
            }
            printWindow.print();
            setTimeout(() => {
              printWindow.close();
              resolve();
            }, 100);
          }
        } catch (e) {
          // Fallback to regular printing if silent approach fails
          console.warn('Silent printing setup failed, using regular print:', e);
          printWindow.print();
          setTimeout(() => {
            printWindow.close();
            resolve();
          }, 100);
        }
      } else {
        // Regular printing with dialog
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
          resolve();
        }, 100);
      }
    }, 300);
  });
};

// Original print functions (for backward compatibility)
export const printReceipt = (order: Order, branchInfo?: { name?: string; address?: string; phone?: string; logo?: string; currency?: string }) => {
  return printReceiptWithPrinter(order, branchInfo);
};

// Enhanced print functions with printer support
export const printReceiptWithPrinter = async (order: Order, branchInfo?: { name?: string; address?: string; phone?: string; logo?: string; currency?: string }, printerName?: string, silent: boolean = false) => {
  try {
    const printWindow = window.open('', '', 'width=450,height=auto,scrollbars=yes');
    if (!printWindow) {
      const errorMsg = 'Popup blocked! Please allow popups for automatic receipt printing to work.';
      console.error('🚫 Print blocked:', errorMsg);
      
      // Dispatch event to notify UI about popup block
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('print-popup-blocked', {
          detail: { 
            type: 'receipt',
            orderId: order.id, 
            orderNumber: order.order_number
          }
        }))
      }
      
      throw new Error(errorMsg);
    }

    const printContainer = document.createElement('div');
    const root = createRoot(printContainer);
    
    root.render(
      <PrintReceipt 
        order={order} 
        restaurantName={branchInfo?.name}
        restaurantLogo={branchInfo?.logo}
        branchAddress={branchInfo?.address}
        branchPhone={branchInfo?.phone}
        currency={branchInfo?.currency}
      />
    );

    // Wait for render and then print
    setTimeout(async () => {
      try {
        await executePrint(
          printWindow, 
          printContainer.innerHTML, 
          `Receipt - Order #${order.order_number}`,
          printerName,
          silent
        );
        console.log('✅ Receipt print initiated successfully');
      } catch (error) {
        console.error('❌ Print execution failed:', error);
        printWindow.close();
        throw error;
      }
    }, 200); // Slightly increased delay for better reliability
  } catch (error) {
    console.error('❌ Receipt printing failed:', error);
    throw error;
  }
};

// Original kitchen ticket function (for backward compatibility)
export const printKitchenTicket = (order: Order, branchInfo?: { name?: string }) => {
  return printKitchenTicketWithPrinter(order, branchInfo);
};

// Enhanced kitchen ticket function with printer support
export const printKitchenTicketWithPrinter = async (order: Order, branchInfo?: { name?: string }, printerName?: string, silent: boolean = false) => {
  const printWindow = window.open('', '', 'width=500,height=auto,scrollbars=yes');
  if (!printWindow) {
    alert('Please allow popups to print kitchen tickets');
    return;
  }

  const printContainer = document.createElement('div');
  const root = createRoot(printContainer);
  
  root.render(
    <PrintKitchenTicket 
      order={order} 
      branchName={branchInfo?.name}
    />
  );

  // Wait for render and then print
  setTimeout(async () => {
    await executePrint(
      printWindow, 
      printContainer.innerHTML, 
      `Kitchen Ticket - Order #${order.order_number}`,
      printerName,
      silent
    );
  }, 100);
}; 